/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
// TODO: Add types for mixpanel and IA: https://lastbrand.atlassian.net/browse/SFPLTFRM-873
import { IS_PROD_ENV, IS_PROD_BUILD } from '@src/config/env'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { CustomAttribute } from '@src/quince-site-common/src/domains/shopify/CustomAttribute'
import { getShopifyVariantId } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { computeScrollPercentage } from '@src/quince-site-common/src/hooks/useScrollEventTracker'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getAppAnalyticsData } from '@src/quince-site-common/src/utils/cross-platform-analytics/cross-platform-analytics.utils'
import { decodeShopifyIdFromGraphqlIdResponse } from '@src/quince-site-common/src/utils/decodeShopifyIdFromGraphqlIdResponse'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getOSDetails, getUserBrowser } from '@src/quince-site-common/src/utils/getDeviceDetailsFromUserAgent'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getProductTagsDataForBatchedAnalyticsFromTags } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'

const is_prod = isBrowser() && IS_PROD_ENV && IS_PROD_BUILD

export const getCommonProperties = (properties: any) => {
  const url = new URL(properties.url)
  const urlParamObj: Record<string, string> = {}
  url.searchParams.forEach((val: string, key: string) => {
    urlParamObj[key] = val
  })
  const browserDetails = getUserBrowser()
  const osDetails = getOSDetails()
  const eventTimestamp = new Date(properties.event_time).getTime()
  const { context: app_context_data, ...appRestEventData } = getAppAnalyticsData()

  const commonProperties = {
    ...appRestEventData,
    app_context_data,
    event_id: properties.id,
    event_timestamp: eventTimestamp,
    active_experiment: properties.active_experiments,
    page_url: properties.url,
    visit_url: properties.url,
    visit_page: url.pathname,
    visit_url_params: urlParamObj,
    device_type: properties.device,
    session_id:
      properties.session_id ||
      getCookie(ANALYTIC_COOKIES.SESSION_ID) ||
      getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.SESSION_ID),
    is_logged_in: AuthUtil.isLoggedIn(),
    tentative_email: getCookie(ANALYTIC_COOKIES.MIXPANEL_TENTATIVE_EMAIL),
    $device_id: getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_DEVICE_ID),
    $user_id:
      getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_ID) ??
      getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.MIXPANEL_USER_ID) ??
      '',
    $email:
      getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_ID) ?? getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.MIXPANEL_USER_ID),
    lb_user_id: getlbUserId(),
    page_name: properties?.page_name ?? properties?.context?.page_name,
    page_type: properties?.page_type,
    previous_page_name: properties?.previous_page_name,
    previous_page_type: properties?.previous_page_type,
    is_in_first_fold: computeScrollPercentage() === 0 ? 1 : 0,
    zip_code: getCookie(ANALYTIC_COOKIES.USER_ENTERED_ZIP_CODE) ?? getCookie(ANALYTIC_COOKIES.ZIP_CODE),
    is_prod,
  }

  return {
    sgtmCommonProperties: {
      ...commonProperties,
      ip: getCookie(ANALYTIC_COOKIES.ECOMM_IP),
      $os: properties.device_category,
      $os_version: osDetails.osVersion,
      $browser: browserDetails.browserName,
      $browser_version: browserDetails.browserVersion,
      time: eventTimestamp,
      $insert_id: properties.id,
      utm_source: getCookie(ANALYTIC_COOKIES.UTM_SOURCE),
      utm_medium: getCookie(ANALYTIC_COOKIES.UTM_MEDIUM),
      utm_campaign: getCookie(ANALYTIC_COOKIES.UTM_CAMPAIGN),
      utm_content: getCookie(ANALYTIC_COOKIES.UTM_CONTENT),
    },

    clientCommonProperties: {
      ...commonProperties,
    },
  }
}

export const getTransformedProperties = (properties: Record<string, unknown>): Record<string, unknown> | null => {
  switch (properties.event_type) {
    case 'page_view':
      return {
        event_type: 'page_load',
        event_name: 'Page Viewed',
      }

    case 'home_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Home Page Viewed',
      }

    case 'wishlist_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Wishlist Page Viewed',
      }

    case 'search_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Search Page Viewed',
      }

    case 'collection_view':
      return {
        event_type: 'page_load',
        event_name: properties.context?.isDynamicCollectionPage
          ? 'Dynamic Collection Page Viewed'
          : 'Collection Page Viewed',
      }

    case 'product_view':
      return {
        event_type: 'page_load',
        event_name: 'PDP Viewed',
        product_id: properties.context?.product_id,
        name: properties.context?.name,
        products: properties.context?.name ? [properties.context.name] : [],
        division: properties.context?.division,
        subdepartment: properties.context?.subDepartment,
        department: properties.context?.department,
        gender: properties.context?.gender,
        findingMethodAggregate: properties?.context?.findingMethodAggregate,
        findingMethodGranular: properties?.context?.findingMethodGranular,
        findingMethodId: properties?.context?.findingMethodId,
        ...properties?.context?.productProperties,
      }

    case 'is_visible': {
      const element_name =
        properties.context?.contentContainerName ||
        properties.entity_id ||
        properties.context?.childToParentTrackingIds?.[0]

      let event_name = 'impression'
      if (properties.entity_type === 'product_card_view') {
        event_name = 'Product List Viewed'
      } else if (properties.context?.contentContainerType === CUSTOM_SECTION_CONTAINER_TYPES.CATEGORY_DISPLAY) {
        if (!properties.context?.category_name) return
        event_name = `${properties.context.contentContainerType} Viewed`
      } else if (properties.context?.contentContainerType) {
        event_name = `${properties.context.contentContainerType} Viewed`
      }

      let modifiedProperties: any = {
        event_type: 'impression',
        event_name: event_name,
        element_name: element_name,
        element_id: properties.context?.trackingId || properties.context?.id,
        element_display_name:
          properties.context?.contentContainerDisplayName ||
          properties.context?.displayTitle ||
          properties.context?.childToParentTrackingIds?.[0] ||
          properties.entity_id,
        element_type: properties.context?.contentContainerType,
        category_name: properties.context?.category_name,
        category_position: properties.context?.category_position,
        element_position:
          properties.context?.contentContainerPosition !== undefined
            ? properties.context.contentContainerPosition
            : properties.context?.elementPosition,
      }

      if (properties.entity_type === 'product_card_view') {
        modifiedProperties.product_count = properties.context?.products_list?.length || 0
        modifiedProperties.product_id = (properties.context?.products_list || []).map(
          (product: { shopifyProductId: number }) => product.shopifyProductId.toString(),
        )
        modifiedProperties.name = (properties.context?.products_list || []).map(
          (product: { name: string }) => product.name,
        )
        modifiedProperties.products = (properties.context?.products_list || []).map(
          (product: {
            color: string
            product_id: number
            product_options: { name: string; color: string }[]
            tags: string[]
            oos_flag: boolean
            name: string
            color_variant: string[]
            count_color_variant: number
            rating: number | string
            wishlishted: boolean
            is_new: boolean
            back_in_stock: boolean
            sold_out: boolean
            low_stock: boolean
            best_seller: boolean
            shopifyProductId: number
            productProperties: ProductProperties
          }) => ({
            ...product,
            wishlishted: product.wishlishted !== undefined && (product.wishlishted ? 1 : 0),
          }),
        )

        modifiedProperties = {
          ...modifiedProperties,
          ...getProductTagsDataForBatchedAnalyticsFromTags(properties?.context?.products_list || []),
        }
      }

      return modifiedProperties
    }

    case 'product_click': {
      const element_type = properties.context?.contentContainerType
      const event_name =
        element_type && properties.event_type !== 'product_click' ? `${element_type} Clicked` : 'Product Card Clicked'
      const element_name =
        properties.context?.contentContainerName || properties.context?.sectionType || properties.entity_id
      const element_display_name =
        properties.context?.contentContainerDisplayName || properties.context?.tracker_section
      return {
        ...properties?.context?.productProperties,
        event_type: 'click',
        event_name: event_name,
        element_name: element_name,
        element_display_name: element_display_name,
        element_type: element_type,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        element_position:
          properties.context?.contentContainerPosition !== undefined
            ? properties.context.contentContainerPosition
            : properties.context?.elementPosition,
        product_id: properties.context?.product_id,
        color: properties.context?.product_color || properties.context?.color,
        name: properties.context?.name,
        color_variant: properties.context?.color_variant,
        count_color_variant: properties.context?.count_color_variant,
        rating: properties.context?.rating,
        product_position: properties.context?.productPosition ? Number(properties.context.productPosition) + 1 : 0,
        tags: properties.context?.tags,
        is_new: properties.context?.is_new,
        back_in_stock: properties.context?.back_in_stock,
        sold_out: properties.context?.sold_out,
        low_stock: properties.context?.low_stock,
        best_seller: properties.context?.best_seller,
        ...properties?.content?.productProperties,
      }
    }

    case 'button_click': {
      const element_name =
        properties.context?.contentContainerName ||
        properties.context?.childToParentTrackingIds?.reverse().join('__') ||
        properties.context?.label ||
        properties.context?.element_name ||
        'Button'
      const lineItems = properties.entity_id === 'start_checkout' ? properties.context?.lineItems || [] : []
      const productIdsSet = new Set(
        lineItems.map(li => li.customAttributes?.find((ca: CustomAttribute) => ca.key === '_productId').value),
      )
      const productIdsArr = Array.from(productIdsSet)
      const formattedLineItems = lineItems.map(li => {
        const shopifyVariantId = getShopifyVariantId(li)
        return {
          sku: li.variant.sku,
          name: li.title,
          price: li.variant.priceV2.amount ? Number(li.variant.priceV2.amount) : undefined,
          color: li.variant.selectedOptions.find((so: ProductVariantOption) => so.name === 'color')?.value,
          size: li.variant.selectedOptions.find((so: ProductVariantOption) => so.name === 'Size')?.value,
          variant_id: shopifyVariantId ? decodeShopifyIdFromGraphqlIdResponse(shopifyVariantId) : undefined,
        }
      })
      let event_name = `${properties.context?.contentContainerType || element_name} Clicked`
      if (properties.entity_id === 'start_checkout') {
        event_name = 'Checkout Initiated'
      } else if (properties.entity_id === 'click_menu_link') {
        event_name = 'Top Nav Clicked'
      } else if (properties.entity_id == 'click_quick_shop') {
        event_name = 'Product Popup Viewed'
      } else if (element_name == 'menu_display_state') {
        event_name = 'Mobile Menu Opened'
      } else if (properties?.context?.element_name == 'Product Display') {
        event_name = 'Product Display Clicked'
      } else if (properties.entity_id === 'header_mega_menu_toggle') {
        event_name = 'Mobile Menu Navigation'
      } else if (properties.entity_id === 'attentive_email_submitted') {
        event_name = 'Signup Attributes Added'
      } else if (properties?.entity_id === 'click_next_reviews_page') {
        event_name = 'View more reviews clicked'
      } else if (properties.entity_id === 'return_initiated') {
        event_name = 'Return Initiated'
      } else if (properties.entity_id === 'return_successful') {
        event_name = 'Return Successful'
      } else if (properties?.entity_id === 'search_button_click') {
        event_name = 'Search Clicked'
      } else if (properties?.entity_id === 'easy_returns') {
        event_name = 'Easy Returns Clicked'
      }
      else if (properties?.entity_id === 'shipping_delayed_contact_clicked') {
        event_name = 'Shipping Delayed Contact Clicked'
      }
      return {
        event_type: properties.entity_id === 'start_checkout' ? 'checkout' : 'click',
        event_name: event_name,
        nav_element:
          properties.entity_id === 'click_menu_link' ? properties.context?.menuTitle : properties.context?.nav_element,
        top_nav_element: properties.entity_id === 'click_menu_link' ? properties.context?.menuTitle : undefined,
        element_name: element_name,
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        price: properties.context?.product_price ? Number(properties.context.product_price) : undefined,
        element_type: properties.context?.contentContainerType,
        element_display_name:
          properties.context?.contentContainerDisplayName || properties.context?.element_display_name,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        product_price: properties.context?.product_price,
        product_count: productIdsArr.length,
        cart_value: properties.context?.totalPrice ? Number(properties.context.totalPrice) : undefined,
        variants: formattedLineItems,
        name: properties.context?.name,
        color: properties.context?.color_name,
        color_variant: properties.context?.color_variant,
        rating: properties.context?.rating,
        custom_variants: properties.context?.custom_variants,
        oos_flag: properties.context?.oos_flag,
        gender: properties.context?.gender,
        creativeId: properties.context?.creativeId,
        ...properties?.context.productProperties,
        L1: properties?.context?.L1,
        L2: properties?.context?.L2,
        L3: properties?.context?.L3,
      }
    }

    case 'link_click': {
      const element_display_name =
        properties.context?.contentContainerDisplayName ||
        properties.context?.displayTitle ||
        properties.context?.sectionType
      let event_name = 'Link Clicked'
      if (properties?.context?.contentContainerType) {
        event_name = `${properties?.context?.contentContainerType} Clicked`
      } else if (properties?.entity_id === 'account_link_click') {
        event_name = 'Profile Clicked'
      } else if (properties?.entity_id === 'wishlist_link_click') {
        event_name = 'Wishlist Clicked'
      }

      return {
        event_type: 'click',
        event_name: event_name,
        category_name: properties.context?.label !== undefined ? [properties.context.label] : undefined,
        category_position:
          properties.context?.col !== undefined && properties.context.row !== undefined
            ? [Number(properties.context.col) * 2 + (Number(properties.context.row) + 1)]
            : undefined,
        element_display_name: element_display_name,
        element_name: properties.context?.contentContainerName,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        element_type: properties.context?.contentContainerType,
        element_position:
          properties.context?.contentContainerPosition !== undefined
            ? properties.context.contentContainerPosition
            : properties.context?.elementPosition,
        horizontal_position: properties?.context?.horizontalPosition,
      }
    }

    case 'klaviyo_newsletter_subscribe':
      return {
        event_type: 'email_subscription',
        event_name: 'Email Subscribed',
        subscription_succesful: 1,
      }

    case 'page_scroll':
      return {
        event_type: 'page_scroll',
        event_name: 'Page Scrolled',
        scroll_percentage: properties.entity_id,
      }

    case 'filter_click':
      return {
        event_type: 'click',
        event_name: 'Dropdown filter used',
        filter_used: properties.context?.label,
        filter_value: properties.context?.value,
      }

    case 'buy_now':
      return {
        event_type: 'click',
        event_name: 'Buy Now Clicked',
        variants: properties?.context?.line_items,
        ...properties?.context?.productProperties,
      }

    case 'variant_select':
      return {
        event_type: 'click',
        event_name: 'Product Option Selected',
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        division: properties.context?.division,
        department: properties.context?.department,
        subdepartment: properties.context?.subdepartment,
        product_option_name: properties.context?.product_option_name,
        product_option_value: properties.context?.product_option_value,
        message_displayed: properties.context?.message_displayed,
        element_name: properties.context?.element_name || null,
        element_display_name: properties.context?.element_display_name || null,
        element_type: properties.context?.element_type || null,
        element_id: properties.context?.element_id || null,
        element_position: properties.context?.element_position || null,
        is_limited_edition_color: properties.context?.is_limited_edition_color,
        ...properties?.context?.productProperties,
      }

    case 'add_to_wishlist':
      return {
        event_type: 'click',
        event_name: 'Added to wishlist',
        email_added: properties.context?.product_id,
        product_id: properties.context?.product_id,
        element_name: properties.context?.tracker_section,
        color_name: properties.context?.color,
      }

    case 'remove_from_wishlist':
      return {
        event_type: 'click',
        event_name: 'Removed from wishlist',
        product_id: properties.context?.product_id,
        element_name: properties.context?.tracker_section,
        color_name: properties.context?.color,
        ...properties?.context?.productProperties,
      }

    case 'product_thumbnail_zoom':
      return {
        event_type: 'hover',
        event_name: 'Product Thumbnail Zoomed',
        product_id: properties.context?.product_id,
        ...properties?.context?.productProperties,
      }

    case 'join_the_waitlist': {
      return {
        event_type: 'click',
        event_name: 'Waitlist Joined',
        ...properties?.context?.productProperties,
      }
    }

    case 'add_to_cart': {
      const latestLineItem = properties.context?.line_items
        ? properties.context.line_items?.find(
            lineItem => lineItem?.variantId && lineItem?.variantId == properties.entity_id,
          )
        : undefined
      return {
        event_type: 'click',
        event_name: 'Added to Cart',
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        sku: latestLineItem?.sku,
        variant_id: latestLineItem?.variantId ? latestLineItem.variantId.toString() : undefined,
        name: latestLineItem?.title,
        price: latestLineItem?.price ? Number(latestLineItem?.price) : undefined,
        color: latestLineItem?.variantTitle.split(' / ')[1],
        size_variant: latestLineItem?.variantTitle.split(' / ')[0],
        products: [latestLineItem?.title],
        division: properties.context?.division,
        department: properties.context?.department,
        subdepartment: properties.context?.subDepartment,
        // We get cart_upsell_${trackerConfigKey} as trackerSection for mobile view quick shop modal.
        quick_add_to_cart:
          properties.context?.tracker_section === 'quick_shop' ||
          properties.context?.tracker_section === 'social_section' ||
          properties?.context?.tracker_section?.includes('cart_upsell_')
            ? 1
            : 0,
        color_variant: properties.context?.color_variant,
        gender: properties.context?.gender,
        ...properties?.context.productProperties,
        findingMethodAggregate: properties?.context?.findingMethodAggregate,
        findingMethodGranular: properties?.context?.findingMethodGranular,
        findingMethodId: properties?.context?.findingMethodId,
        edd_standard_eligible: properties?.context?.edd_standard_eligible,
        edd_express_eligible: properties?.context?.edd_express_eligible,
        edd_max_days: properties?.context?.edd_max_days,
        edd_min_days: properties?.context?.edd_min_days,
      }
    }
    case 'clicked_to_search':
      return {
        event_type: 'search',
        event_name: 'Search Initiated',
      }

    case 'search_triggered':
      return {
        event_type: 'search',
        event_name: 'Search Triggered',
        search_query: properties.context.search_query,
        query_length: properties.context.query_length,
        autocomplete_count: properties.context.autocomplete_count,
        autocomplete_selected: properties.context.autocomplete_selected,
        autocomplete_rank: properties.context.autocomplete_rank,
        autocomplete_term: properties.context.autocomplete_term,
        search_cancel: properties.context.search_cancel,
      }

    case 'top_nav_opened':
      return {
        event_type: 'menu_opened',
        event_name: 'Top Nav Opened',
        top_nav_element: properties.context.top_nav_element,
        label_visible: properties.context?.label_visible,
      }

    case 'profile_menu_opened':
      return {
        event_type: 'menu_opened',
        event_name: 'Profile Menu Opened',
      }

    case 'header_logo_clicked':
      return {
        event_type: 'click',
        event_name: 'Header Logo Clicked',
      }

    case 'sort_opened':
      return {
        event_type: 'click',
        event_name: 'Sort Opened',
      }

    case 'sort_used':
      return {
        event_type: 'click',
        event_name: 'Sort Used',
        sort_used: properties.context?.sort_used,
        ...properties?.context?.productProperties,
      }

    case 'faq_viewed':
      return {
        event_type: 'impression',
        event_name: 'FAQ Viewed',
        no_of_FAQ_cards: properties.context?.no_of_FAQ_cards,
      }

    case 'faq_clicked':
      return {
        event_type: 'click',
        event_name: 'FAQ Clicked',
        FAQ_description: properties.context?.FAQ_description,
        ...properties?.context?.productProperties,
      }

    case 'address_addition_initiated':
      return {
        event_type: 'click',
        event_name: 'Address Addition Initiated',
        default_address_present: properties.context?.default_address_present,
        no_of_address: properties.context?.no_of_address,
      }

    case 'address_addition_successful':
      return {
        event_type: 'click',
        event_name: 'Address Addition Successful',
        default_address_present: properties.context?.default_address_present,
        no_of_address: properties.context?.no_of_address,
      }

    case 'address_edit_successful':
      return {
        event_type: 'click',
        event_name: 'Edit Address Successful',
        is_default_address: properties.context?.is_default_address,
        no_of_address: properties.context?.no_of_address,
      }

    case 'product_gallery_viewed':
      return {
        event_type: 'impression',
        event_name: 'Product Gallery Viewed',
        sku: properties.context?.sku,
        product_id: properties.context?.product_id,
        variant_id: properties.context?.variant_id,
      }

    case 'reviews_sorted':
      return {
        event_type: 'click',
        event_name: 'Reviews Sorted',
        sku: properties.context?.sku,
        product_id: properties.context?.product_id,
        variant_id: properties.context?.variant_id,
        has_photo_filter: properties.context?.has_photo_filter,
        ...properties?.context?.productProperties,
      }

    case 'search_completed':
      return {
        event_type: 'search',
        event_name: 'Search Completed',
        null_search: properties.context?.null_search,
        result_count: properties.context?.result_count,
        products: properties.context?.products,
        oos_product_count: properties.context?.oos_product_count,
        ...properties?.context.productProperties,
      }

    case 'similar_collection_clicked':
      return {
        event_type: 'click',
        event_name: 'Similar Collection Clicked',
        filter_available: properties.context?.filter_available,
        filter_count: properties.context?.filter_count,
        filter_used: properties.context?.filter_used,
      }

    case 'subcollection_filter_used':
      return {
        event_type: 'click',
        event_name: 'Subcollection Filter Used',
        filter_used: properties.context?.filter_used,
        filter_available: properties.context?.filter_available,
      }
    case 'remove_from_cart': {
      return {
        event_type: 'click',
        event_name: 'Removed from Cart',
        variant_id: properties.entity_id,
        productProperties: properties?.context.productProperties,
      }
    }

    case 'subcollection_card_click': {
      return {
        event_type: 'click',
        event_name: 'Subcategory Filter Used',
        filter_used: properties.context?.subcollection_title,
      }
    }

    case 'variant_selected':
      return {
        event_type: 'click',
        event_name: 'Variant Selected',
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        color: properties.context?.color,
        size: properties.context?.size,
        sku: properties.context?.sku,
        variant_id: properties.context?.variant_id ? properties.context.variant_id.toString() : undefined,
        department: properties.context?.department,
        subdepartment: properties.context?.subdepartment,
        division: properties.context?.division,
        age: properties.context?.age,
        is_limited_edition_color: properties.context?.is_limited_edition_color,
        ...properties?.context?.productProperties,
        oos_flag: properties.context?.oos_flag,
        is_preorder: properties.context?.is_preorder,
        is_waitlist: properties.context?.is_waitlist,
      }

    case 'form_open': {
      if (properties.entity_id === 'attentive_popup_opened') {
        const eventType = 'impression'
        const eventName = 'Signup Popup Viewed'
        return {
          event_type: eventType,
          event_name: eventName,
          creative_id: properties.context?.creative_id,
        }
      }
      return null
    }

    case 'form_submit':
      return {
        event_type: properties.context?.action == 'account_login' ? 'login' : 'signup',
        event_name: properties.context?.action == 'account_login' ? 'User Logged In' : 'User Signed Up',
        email: properties.context?.email,
        signup_method: properties.context?.signup_method,
        login_method: properties.context?.login_method,
      }

    case 'product_gallery_scrolled':
      return {
        event_type: 'impression',
        event_name: 'Product Gallery Scrolled',
        name: properties.context?.name,
        department: properties.context?.department,
        division: properties.context?.division,
        subdepartment: properties.context?.subdepartment,
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        ...properties?.context?.productProperties,
      }

    case 'send_referral_email_invite': {
      return {
        event_type: 'referral',
        event_name: 'Referral Initiated',
      }
    }
    case 'product_card_scrolled':
      return {
        event_type: 'impression',
        event_name: 'Product Card Scrolled',
        name: properties?.context?.name,
        department: properties?.context?.department,
        division: properties?.context?.division,
        subdepartment: properties?.context?.subdepartment,
        product_id: properties?.context?.product_id ? properties?.context?.product_id.toString() : undefined,
        ...properties?.context?.productProperties,
      }

    case 'return_line_items_screen_viewed': {
      return {
        event_type: 'return_line_items_screen_viewed',
        event_name: 'Return Line Items Screen Viewed',
      }
    }
    case 'return_fee_tooltip_hover': {
      return {
        event_type: 'return_fee_tooltip_hovered',
        event_name: 'Return Fee Tooltip Hovered',
      }
    }
    case 'submit_line_items_cta_clicked': {
      return {
        event_type: 'submit_line_items_cta_clicked',
        event_name: 'Return Line Items Selected',
      }
    }
    case 'refund_amount_screen_viewed': {
      return {
        event_type: 'refund_amount_screen_viewed',
        event_name: 'Return Summary Page Viewed',
      }
    }
    case 'return_confirm_cta_clicked': {
      return {
        event_type: 'return_confirm_cta_clicked',
        event_name: 'Return Confirm CTA Clicked',
      }
    }
    case 'return_confirm_screen_viewed': {
      return {
        event_type: 'return_confirm_screen_viewed',
        event_name: 'Return Confirmed Page Viewed',
      }
    }
    case 'cart_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Cart Viewed',
      }

    default:
      return null
  }
}

export const isBotAgent = () => {
  if (navigator?.userAgent) {
    const ua = navigator.userAgent
    const botUaNames = ['facebookexternalhit', 'facebookcatalog', 'Googlebot', 'remotasks', 'adsbot']
    let isBot = false
    botUaNames.forEach(nm => {
      if (ua.includes(nm)) {
        isBot = true
      }
    })
    return isBot
  }
  return false
}
